import {
  ShippingProviderType,
  type Shipment as CarrierShipment,
} from "@redotech/redo-fulfillment-carrier-common/shipment";
import {
  BLUE_YONDER_PROVIDER,
  dropoffProviderType,
  HAPPY_RETURNS_PROVIDER,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
  type DropoffProviderType,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { getVirtualMiddleMileCarrierName } from "@redotech/redo-return-dropoff-provider-common/virtual-middle-mile";
import { assertNever } from "@redotech/util/type";
import {
  getDropoffIdentifierForProduct,
  getDropoffIdentifierForProvider,
  getVirtualMiddleMileCarrier,
  isDropoffChosen,
} from "../../dropoff/utils";
import type { ReturnZod } from "../../return";
import { TrackstarReturnProvider } from "../../return-integration-types";
import {
  getShipmentForProduct,
  productMatchesShipmentGroup,
  type ProductShipmentGroupFields,
} from "../../shipping/shipment-group";

export const BLUEYONDER_INTEGRATION = "BlueYonder";
export const HAPPY_RETURNS_INTEGRATION = "HappyReturns";
export const NARVAR_INTEGRATION = "Narvar";
export const RETURNBEAR_INTEGRATION = "ReturnBear";
export const RETURNLOGIC_INTEGRATION = "ReturnLogic";

export enum ExternalRmaIntegration {
  AIMS360 = "Aims360",
  APPAREL_MAGIC = "ApparelMagic",
  ASTRO = "Astro",
  BLUE_CHERRY = "BlueCherry",
  BRIGHTPEARL = "Brightpearl",
  CIN7_OMNI = "Cin7Omni",
  CLOUDX = "CloudX",
  CONTROLPORT = "ControlPort",
  DEPOSCO = "Deposco",
  ECWID = "Ecwid",
  EXTENSIV = "Extensiv",
  FLEXPORT = "Flexport",
  GLOBALE = "GlobalE",
  INDIGO8 = "Indigo8",
  INMAR = "Inmar",
  LINNWORKS = "LinnworksDirect",
  MASONHUB = "Masonhub",
  MINTSOFT = "Mintsoft",
  NETSUITE = "NetSuite",
  ODOO = "Odoo",
  RYDER = "Ryder",
  SELLERCLOUD = "Sellercloud",
  SFOM = "SFOM",
  SHIPBOB = "ShipBob",
  SHIPEDGE = "Shipedge",
  SHIPHERO = "ShipHero",
  SHIPMONK = "ShipMonk",
  TRACKSTAR = "Trackstar",
}

export const externalRmaIntegrationDisplayNames: Record<
  ExternalRmaIntegration,
  string
> = {
  [ExternalRmaIntegration.AIMS360]: "Aims360",
  [ExternalRmaIntegration.APPAREL_MAGIC]: "Apparel Magic",
  [ExternalRmaIntegration.ASTRO]: "Astro",
  [ExternalRmaIntegration.BLUE_CHERRY]: "BlueCherry",
  [ExternalRmaIntegration.BRIGHTPEARL]: "Brightpearl",
  [ExternalRmaIntegration.CIN7_OMNI]: "Cin7 Omni",
  [ExternalRmaIntegration.CLOUDX]: "CloudX/Bergen Logistics",
  [ExternalRmaIntegration.CONTROLPORT]: "ControlPort",
  [ExternalRmaIntegration.DEPOSCO]: "Deposco",
  [ExternalRmaIntegration.ECWID]: "Ecwid",
  [ExternalRmaIntegration.EXTENSIV]: "Extensiv",
  [ExternalRmaIntegration.FLEXPORT]: "Flexport",
  [ExternalRmaIntegration.GLOBALE]: "Global-e",
  [ExternalRmaIntegration.INDIGO8]: "Indigo8",
  [ExternalRmaIntegration.INMAR]: "Inmar",
  [ExternalRmaIntegration.LINNWORKS]: "Linnworks",
  [ExternalRmaIntegration.MASONHUB]: "MasonHub",
  [ExternalRmaIntegration.MINTSOFT]: "Mintsoft",
  [ExternalRmaIntegration.NETSUITE]: "NetSuite",
  [ExternalRmaIntegration.ODOO]: "Odoo",
  [ExternalRmaIntegration.RYDER]: "Ryder",
  [ExternalRmaIntegration.SELLERCLOUD]: "Sellercloud",
  [ExternalRmaIntegration.SFOM]: "SFOM",
  [ExternalRmaIntegration.SHIPBOB]: "ShipBob",
  [ExternalRmaIntegration.SHIPEDGE]: "Shipedge",
  [ExternalRmaIntegration.SHIPHERO]: "ShipHero",
  [ExternalRmaIntegration.SHIPMONK]: "ShipMonk",
  [ExternalRmaIntegration.TRACKSTAR]: "Trackstar",
};

export const CreateExternalIntegrationIdProvider = {
  ...ExternalRmaIntegration,
  BLUEYONDER: BLUEYONDER_INTEGRATION,
  HAPPY_RETURNS: HAPPY_RETURNS_INTEGRATION,
  NARVAR: NARVAR_INTEGRATION,
  RETURNBEAR: RETURNBEAR_INTEGRATION,
  RETURNLOGIC: RETURNLOGIC_INTEGRATION,
} as const;
export type CreateExternalIntegrationIdProvider =
  (typeof CreateExternalIntegrationIdProvider)[keyof typeof CreateExternalIntegrationIdProvider];

export enum CreateExternalIntegrationIdType {
  RMA = "RMA",
  RETURN_BARCODE = "RETURN_BARCODE",
  SUPPORT_CASE = "SUPPORT_CASE",
  CASH_REFUND = "CASH_REFUND",
  CREDIT_MEMO = "CREDIT_MEMO",
  REFUND = "REFUND",
  SALES_CREDIT = "SALES_CREDIT",
  SALES_CREDIT_PAYMENT = "SALES_CREDIT_PAYMENT",
  RECEIPT = "RECEIPT",
  SALES_ORDER = "SALES_ORDER",
  VENDOR_RETURN_AUTHORIZATION = "VENDOR_RETURN_AUTHORIZATION",
  BOX = "BOX",
  BOX_BARCODE = "BOX_BARCODE",
}

const createProviderAllowedTypes = {
  [CreateExternalIntegrationIdProvider.AIMS360]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.APPAREL_MAGIC]: [
    CreateExternalIntegrationIdType.RMA,
    CreateExternalIntegrationIdType.CREDIT_MEMO,
  ],
  [CreateExternalIntegrationIdProvider.BLUE_CHERRY]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.BRIGHTPEARL]: [
    CreateExternalIntegrationIdType.SALES_CREDIT,
  ],
  [CreateExternalIntegrationIdProvider.CIN7_OMNI]: [
    CreateExternalIntegrationIdType.SALES_CREDIT,
    CreateExternalIntegrationIdType.SALES_CREDIT_PAYMENT,
  ],
  [CreateExternalIntegrationIdProvider.ASTRO]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.CLOUDX]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.CONTROLPORT]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.DEPOSCO]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.ECWID]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.EXTENSIV]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.FLEXPORT]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.GLOBALE]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.INDIGO8]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.INMAR]: [
    CreateExternalIntegrationIdType.BOX,
    CreateExternalIntegrationIdType.BOX_BARCODE,
  ],
  [CreateExternalIntegrationIdProvider.LINNWORKS]: [
    CreateExternalIntegrationIdType.RMA,
    CreateExternalIntegrationIdType.SALES_ORDER,
  ],
  [CreateExternalIntegrationIdProvider.MASONHUB]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.MINTSOFT]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.NARVAR]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.NETSUITE]: [
    CreateExternalIntegrationIdType.RMA,
    CreateExternalIntegrationIdType.SUPPORT_CASE,
    CreateExternalIntegrationIdType.RECEIPT,
    CreateExternalIntegrationIdType.CASH_REFUND,
    CreateExternalIntegrationIdType.CREDIT_MEMO,
    CreateExternalIntegrationIdType.REFUND,
    CreateExternalIntegrationIdType.SALES_ORDER,
    CreateExternalIntegrationIdType.VENDOR_RETURN_AUTHORIZATION,
  ],
  [CreateExternalIntegrationIdProvider.ODOO]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.RETURNBEAR]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.RETURNLOGIC]: [
    CreateExternalIntegrationIdType.RMA,
    CreateExternalIntegrationIdType.SALES_ORDER,
  ],
  [CreateExternalIntegrationIdProvider.RYDER]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SELLERCLOUD]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SFOM]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SHIPBOB]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SHIPEDGE]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SHIPHERO]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.SHIPMONK]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.TRACKSTAR]: [
    CreateExternalIntegrationIdType.RMA,
  ],
  [CreateExternalIntegrationIdProvider.BLUEYONDER]: [
    CreateExternalIntegrationIdType.RMA,
    CreateExternalIntegrationIdType.RETURN_BARCODE,
  ],
  [CreateExternalIntegrationIdProvider.HAPPY_RETURNS]: [
    CreateExternalIntegrationIdType.RMA,
    CreateExternalIntegrationIdType.RETURN_BARCODE,
  ],
} as const satisfies Record<
  CreateExternalIntegrationIdProvider,
  readonly CreateExternalIntegrationIdType[]
>;

type CreateAllowedTypesFor<P extends CreateExternalIntegrationIdProvider> =
  (typeof createProviderAllowedTypes)[P][number];

export type ValidCreateProviderTypePair = {
  [P in CreateExternalIntegrationIdProvider]: {
    provider: P;
    type: CreateAllowedTypesFor<P>;
  };
}[CreateExternalIntegrationIdProvider];

export type ValidCreateExternalIntegrationId = ValidCreateProviderTypePair & {
  id: string;
  shipmentGroupId?: string | null;
};

export type CreateExternalIntegrationId = NonNullable<
  ReturnZod["createExternalIntegrationIds"]
>[number];

export type FindCreateExternalIntegrationIdParams =
  ValidCreateProviderTypePair & { shipmentGroupId?: string; id?: string };

export function createExternalIntegrationId(
  params: ValidCreateExternalIntegrationId,
): CreateExternalIntegrationId {
  return params;
}

function matchesCreateExternalIntegrationIdParams(
  extId: CreateExternalIntegrationId,
  params: FindCreateExternalIntegrationIdParams,
): boolean {
  if (extId.provider !== params.provider) {
    return false;
  }
  if (extId.type !== params.type) {
    return false;
  }
  if (
    params.shipmentGroupId !== undefined &&
    extId.shipmentGroupId !== params.shipmentGroupId
  ) {
    return false;
  }
  if (params.id !== undefined && extId.id !== params.id) {
    return false;
  }
  return true;
}

export function findCreateExternalIntegrationId(
  createExternalIntegrationIds:
    | CreateExternalIntegrationId[]
    | undefined
    | null,
  params: FindCreateExternalIntegrationIdParams,
): CreateExternalIntegrationId | undefined {
  if (!createExternalIntegrationIds) {
    return undefined;
  }
  return createExternalIntegrationIds.find((extId) =>
    matchesCreateExternalIntegrationIdParams(extId, params),
  );
}

export function filterCreateExternalIntegrationIds(
  createExternalIntegrationIds:
    | CreateExternalIntegrationId[]
    | undefined
    | null,
  params: FindCreateExternalIntegrationIdParams,
): CreateExternalIntegrationId[] {
  if (!createExternalIntegrationIds) {
    return [];
  }
  return createExternalIntegrationIds.filter((extId) =>
    matchesCreateExternalIntegrationIdParams(extId, params),
  );
}

export function hasCreateExternalIntegrationId(
  createExternalIntegrationIds:
    | CreateExternalIntegrationId[]
    | undefined
    | null,
  params: FindCreateExternalIntegrationIdParams,
): boolean {
  return (
    findCreateExternalIntegrationId(createExternalIntegrationIds, params) !==
    undefined
  );
}

const externalRmaIntegrations: readonly ExternalRmaIntegration[] =
  Object.values(ExternalRmaIntegration);

// Trackstar ShipMonk and Deposco results have always been stored under the
// direct ShipMonk and Deposco providers; keep writing them there.
const trackstarSubProviderResultProvider: Record<
  TrackstarReturnProvider,
  ExternalRmaIntegration
> = {
  [TrackstarReturnProvider.SHIPSTREAM]: ExternalRmaIntegration.TRACKSTAR,
  [TrackstarReturnProvider.BLUEBOX]: ExternalRmaIntegration.TRACKSTAR,
  [TrackstarReturnProvider.SHIPMONK]: ExternalRmaIntegration.SHIPMONK,
  [TrackstarReturnProvider.DEPOSCO]: ExternalRmaIntegration.DEPOSCO,
  [TrackstarReturnProvider.NIMBLE]: ExternalRmaIntegration.TRACKSTAR,
  [TrackstarReturnProvider.WHIPLASH_RYDER]: ExternalRmaIntegration.TRACKSTAR,
  [TrackstarReturnProvider.CIRRO]: ExternalRmaIntegration.TRACKSTAR,
};

/** The provider an RMA result for this integration is stored under. */
export function rmaResultProvider(
  name: ExternalRmaIntegration | TrackstarReturnProvider,
): ExternalRmaIntegration {
  const direct = externalRmaIntegrations.find((provider) => provider === name);
  if (direct) {
    return direct;
  }
  const subProvider = Object.values(TrackstarReturnProvider).find(
    (value) => value === name,
  );
  return subProvider
    ? trackstarSubProviderResultProvider[subProvider]
    : ExternalRmaIntegration.TRACKSTAR;
}

export function isRedoReturnsCenterDestination(return_: {
  merchant_address?: { returnsCenterId?: string | null } | null;
}): boolean {
  return !!return_.merchant_address?.returnsCenterId;
}

/** A dismissed result stops automatic retries for its integration. */
export function shouldSkipRmaResultForIntegration(
  createExternalRmaResults:
    | readonly { provider: ExternalRmaIntegration; status: string }[]
    | null
    | undefined,
  integrationName: ExternalRmaIntegration | TrackstarReturnProvider,
): boolean {
  const provider = rmaResultProvider(integrationName);
  return (
    createExternalRmaResults?.some(
      (result) => result.provider === provider && result.status === "dismissed",
    ) ?? false
  );
}

/** Carrier name reported for each dropoff provider's tracking identifier. */
const BLUE_YONDER_CARRIER = "FedEx";
const RETURNBEAR_CARRIER = "Staples";
const HAPPY_RETURNS_CARRIER = "Happy Returns";

export interface ReturnCarrierAndTracking {
  carrier: string;
  trackingCode: string;
}

// Structural rather than `Pick<ReturnZod, "dropoffs" | "shipment" | "shipments">`:
// callers pass the mongoose `ReturnRaw`, which is NOT assignable to `ReturnZod`
// (e.g. `ReturnRaw.shipment` is `ShipmentRaw | null | undefined`, while
// `ReturnZod.shipment` is `Shipment | undefined` with no `null`, and the
// `ShipmentRaw`/zod `Shipment` shapes diverge). This mirrors the structural
// input convention used by `getReturner`.
interface ReturnShippingSource {
  dropoffs?: {
    chosen?: boolean | null;
    qrCodes:
      | {
          provider?: DropoffProviderType | string | null;
          shipmentGroupId?: string | null;
          trackingNumber?: string | null;
          providerMetadata?: unknown;
        }[]
      | null;
  } | null;
  shipment?: ReturnShippingShipment | null;
  shipments?: ReturnShippingShipment[] | null;
}

export interface ReturnShippingShipment {
  shipmentGroupID?: string | null;
  _shipment?: {
    tracking_code?: string | null;
    tracker?: { tracking_code?: string | null } | null;
    selected_rate?: { carrier?: string | null } | null;
  } | null;
}

/** The `_shipment` payload shape `getReturnCarrierAndTracking` reads. */
export type ExternalLabelShipmentDetails = CarrierShipment;

export interface ExternalLabelShipmentOptions extends ReturnCarrierAndTracking {
  shipmentId: string;
  selectedRateId: string;
  postageLabelId: string;
  currency: string;
  service: string;
}

/**
 * Builds the `_shipment` payload for a label Redo did not purchase, e.g. a
 * marketplace-generated return label. The return type is derived from its consumer.
 */
export function buildExternalLabelShipmentDetails({
  carrier,
  trackingCode,
  shipmentId,
  selectedRateId,
  postageLabelId,
  currency,
  service,
}: ExternalLabelShipmentOptions): ExternalLabelShipmentDetails {
  const timestamp = new Date().toISOString();

  return {
    id: shipmentId,
    status: "",
    shipping_provider_type: ShippingProviderType.EASYPOST,
    tracking_code: trackingCode,
    postage_label: {
      id: postageLabelId,
      label_url: null,
      label_date: timestamp,
    },
    forms: [],
    rates: [],
    selected_rate: {
      id: selectedRateId,
      shipment_id: shipmentId,
      rate: "0",
      currency,
      carrier,
      service,
      delivery_date: null,
      delivery_days: null,
      delivery_date_guaranteed: null,
      created_at: timestamp,
      updated_at: timestamp,
      shipping_provider_type: ShippingProviderType.EASYPOST,
      carrier_account_id: "",
      is_merchant_carrier_account: false,
    },
    tracker: {
      carrier,
      tracking_code: trackingCode,
      public_url: null,
      status: "unknown",
      tracking_details: [],
    },
    customs_info: null,
    insurance: "",
    created_at: timestamp,
    updated_at: timestamp,
  };
}

/**
 * Resolves the carrier and tracking code for a return, preferring a chosen
 * dropoff over the return's own shipment.
 *
 * - Dropoff: a BlueYonder RMA id (FedEx Easy Returns) is used first, then the
 *   chosen dropoff provider's identifier (BlueYonder -> FedEx, Virtual ->
 *   the entry's middle-mile carrier, ReturnBear -> Staples, Happy Returns ->
 *   Happy Returns with its Express Code).
 * - Shipment: when `shipmentGroupId` is given the matching shipment is used,
 *   otherwise the first shipment (falling back to the singular `shipment`).
 *
 * Returns `undefined` unless both a carrier and a (non-empty) tracking code can
 * be resolved — a tracking code with no carrier is not usable.
 */
export function getReturnCarrierAndTracking(
  return_: ReturnShippingSource,
  shipmentGroupId?: string,
): ReturnCarrierAndTracking | undefined {
  return (
    getDropoffCarrierAndTracking(return_, shipmentGroupId) ??
    getShipmentCarrierAndTracking(return_, shipmentGroupId)
  );
}

/**
 * Per-line variant of {@link getReturnCarrierAndTracking}. Matches the
 * product's shipment group(s), and an ungrouped dropoff or shipment covers
 * every product.
 */
export function getProductCarrierAndTracking(
  return_: ReturnShippingSource,
  product: ProductShipmentGroupFields,
): ReturnCarrierAndTracking | undefined {
  return (
    getProductDropoffCarrierAndTracking(return_, product) ??
    toShipmentCarrierAndTracking(
      getShipmentForProduct(return_, product)?._shipment,
    )
  );
}

function getProductDropoffCarrierAndTracking(
  return_: ReturnShippingSource,
  product: ProductShipmentGroupFields,
): ReturnCarrierAndTracking | undefined {
  if (!isDropoffChosen(return_)) {
    return undefined;
  }

  const entry = (return_.dropoffs?.qrCodes ?? []).find((qr) =>
    productMatchesShipmentGroup(product, qr.shipmentGroupId),
  );
  const provider = toDropoffProvider(entry?.provider);
  if (!provider) {
    return undefined;
  }

  const trackingCode = getDropoffIdentifierForProduct(
    return_,
    provider,
    product,
  );
  const carrier = getDropoffCarrier(provider, entry?.providerMetadata);
  return trackingCode && carrier ? { carrier, trackingCode } : undefined;
}

function getDropoffCarrierAndTracking(
  return_: ReturnShippingSource,
  shipmentGroupId?: string,
): ReturnCarrierAndTracking | undefined {
  if (!isDropoffChosen(return_)) {
    return undefined;
  }

  const entry = getChosenDropoffEntry(return_, shipmentGroupId);
  const provider = toDropoffProvider(entry?.provider);
  if (!provider) {
    return undefined;
  }

  const trackingCode = getDropoffIdentifierForProvider(
    return_,
    provider,
    shipmentGroupId,
  );
  const carrier = getDropoffCarrier(provider, entry?.providerMetadata);
  if (!trackingCode || !carrier) {
    return undefined;
  }

  return { carrier, trackingCode };
}

function getDropoffCarrier(
  provider: DropoffProviderType,
  providerMetadata: unknown,
): string | undefined {
  switch (provider) {
    case BLUE_YONDER_PROVIDER:
      return BLUE_YONDER_CARRIER;
    case VIRTUAL_PROVIDER: {
      const carrier = getVirtualMiddleMileCarrier(providerMetadata);
      return carrier && getVirtualMiddleMileCarrierName(carrier);
    }
    case RETURN_BEAR_PROVIDER:
      return RETURNBEAR_CARRIER;
    case HAPPY_RETURNS_PROVIDER:
      return HAPPY_RETURNS_CARRIER;
    default:
      return assertNever(provider);
  }
}

function toDropoffProvider(
  provider: string | null | undefined,
): DropoffProviderType | undefined {
  return dropoffProviderType.find((candidate) => candidate === provider);
}

function getChosenDropoffEntry(
  return_: ReturnShippingSource,
  shipmentGroupId?: string,
) {
  return (return_.dropoffs?.qrCodes ?? []).find(
    (qr) => !shipmentGroupId || qr.shipmentGroupId === shipmentGroupId,
  );
}

function getShipmentCarrierAndTracking(
  return_: ReturnShippingSource,
  shipmentGroupId?: string,
): ReturnCarrierAndTracking | undefined {
  return toShipmentCarrierAndTracking(
    pickShipment(return_, shipmentGroupId)?._shipment,
  );
}

function toShipmentCarrierAndTracking(
  shipment: ReturnShippingShipment["_shipment"],
): ReturnCarrierAndTracking | undefined {
  const trackingCode = (
    shipment?.tracker?.tracking_code ?? shipment?.tracking_code
  )?.trim();
  const carrier = shipment?.selected_rate?.carrier?.trim();

  // Require both — a tracking code with no carrier isn't usable.
  if (!trackingCode || !carrier) {
    return undefined;
  }

  return { carrier, trackingCode };
}

function pickShipment(
  return_: ReturnShippingSource,
  shipmentGroupId?: string,
): ReturnShippingShipment | undefined {
  // When a group is explicitly requested, only that group's shipment is
  // correct — never fall back to another group's shipment, or we'd attach the
  // wrong carrier/tracking to this RMA. The bare fallback is for groupless
  // callers (e.g. Ryder, ShipBob).
  if (shipmentGroupId) {
    return return_.shipments?.find(
      (shipment) => shipment.shipmentGroupID === shipmentGroupId,
    );
  }
  return return_.shipments?.[0] ?? return_.shipment ?? undefined;
}
